import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, CircularProgress, Alert, Button, Paper, TextField } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()]/.test(password);

    if (!minLength) return 'Password must be at least 8 characters long.';
    if (!hasUpperCase) return 'Password must contain at least one uppercase letter.';
    if (!hasLowerCase) return 'Password must contain at least one lowercase letter.';
    if (!hasNumber) return 'Password must contain at least one number.';
    if (!hasSpecialChar) return 'Password must contain at least one special character (!@#$%^&*()).';

    return '';
};

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'form', 'success', 'error'
    const [message, setMessage] = useState('Verifying your request...');
    const [token, setToken] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    useEffect(() => {
        const verificationToken = searchParams.get('token');
        if (!verificationToken) {
            setMessage('Invalid or missing token.');
            setStatus('error');
            return;
        }
        setToken(verificationToken);
        // For the forgot password flow, we show the form immediately.
        // The token validity is checked by the backend when submitting the new password.
        setStatus('form');
        setMessage('Please enter your new password.');
    }, [searchParams]);

    const handleNewPasswordChange = (e) => {
        const newPass = e.target.value;
        setPassword(newPass);
        const error = validatePassword(newPass);
        setNewPasswordError(error);
        if (confirmPassword && newPass !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match.');
        } else {
            setConfirmPasswordError('');
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const newConfirmPass = e.target.value;
        setConfirmPassword(newConfirmPass);
        if (password !== newConfirmPass) {
            setConfirmPasswordError('Passwords do not match.');
        } else {
            setConfirmPasswordError('');
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        const passwordValidationError = validatePassword(password);
        if (passwordValidationError) {
            setNewPasswordError(passwordValidationError);
            return;
        }
        if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match.');
            return;
        }
        setNewPasswordError('');
        setConfirmPasswordError('');
        setStatus('verifying');
        setMessage('Updating your password...');

        try {
            await axios.post(`${API_BASE_URL}/api/auth/reset-password`, { token, newPassword: password });
            setMessage('Password has been updated successfully! Redirecting to login...');
            setStatus('success');
            setTimeout(() => navigate('/'), 3000);
        } catch (err) {
            setMessage(err.response?.data?.message || 'Failed to update password. The link may be expired.');
            setStatus('error');
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f4f6f8' }}>
            <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400, borderRadius: 2, boxShadow: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#ffb300' }}>
                    Reset Password
                </Typography>

                {status === 'verifying' && (
                    <Box sx={{ my: 3 }}><CircularProgress /><Typography sx={{ mt: 1, color: 'text.secondary' }}>{message}</Typography></Box>
                )}

                {status === 'form' && (
                    <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
                        <Typography sx={{ mb: 2, color: 'text.secondary' }}>{message}</Typography>
                        <TextField fullWidth label="New Password" type="password" value={password} onChange={handleNewPasswordChange} margin="normal" required error={!!newPasswordError} helperText={newPasswordError || 'Must be at least 8 characters with uppercase, number, and special character.'} />
                        <TextField fullWidth label="Confirm New Password" type="password" value={confirmPassword} onChange={handleConfirmPasswordChange} margin="normal" required error={!!confirmPasswordError} helperText={confirmPasswordError} />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            color="inherit"
                            sx={{
                                mt: 2,
                                backgroundColor: '#ffb300 !important',
                                color: '#fff',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: '#ffa000 !important',
                                }
                            }}
                        >
                            Reset Password
                        </Button>
                    </Box>
                )}

                {(status === 'success' || status === 'error') && (
                    <Box><Alert severity={status} sx={{ my: 2 }}>{message}</Alert><Button variant="contained" component={Link} to="/" sx={{ mt: 2 }}>Go to Login Page</Button></Box>
                )}
            </Paper>
        </Box>
    );
};

export default ResetPassword;